<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useCMSBySlug } from '../composables/useCMS'
import CMSBanner from './Banner.vue'
import SFShopSwitcher from '~/components/layout/headers/shop-switcher/SFShopSwitcher.vue'
import SFHeaderUserIcons from '~/components/layout/headers/SFHeaderUserIcons.vue'
import type { TypeBannerWrapperSkeleton } from '~/modules/cms/providers/contentful/types'
import { useShopGenderCookie } from '~/composables/useShopGenderCookie'
import { useCurrentShop } from '#storefront/composables'
import SFLocalizedLink from '~/components/SFLocalizedLink.vue'
import { routeList } from '~/utils'

const BANNER_ROTATION_DURATION = 5000
const { cookie: gender } = useShopGenderCookie()
const currentShop = useCurrentShop()

const showStorefinder = computed(() => currentShop.value?.shopId === 1045)

const fetchOptions = computed(() =>
  import.meta.server
    ? { immediate: true, lazy: false }
    : { immediate: false, lazy: true },
)

const genderValue = computed(() =>
  gender.value?.length ? gender.value?.toLowerCase() : 'global',
)
// Make the CMS query reactive so useCMSBySlug re-fetches when the cookie changes
const cmsQuery = computed(() => ({
  content_type: 'bannerWrapper',
  'fields.slug': `${genderValue.value}-banner`,
}))

const { data: bannerWrapper } = await useCMSBySlug<TypeBannerWrapperSkeleton>(
  'banner-wrapper',
  cmsQuery,
  fetchOptions.value,
)

const currentBannerIndex = ref(0)
const isTransitioning = ref(false)
const isPaused = ref(false)

const banners = computed(() => {
  return bannerWrapper.value?.fields.content ?? []
})

const bannerCount = computed(() => {
  return bannerWrapper.value?.fields.content.length ?? 0
})

const bannerRotationDuration = computed(() => {
  if (bannerWrapper.value?.fields.bannerRotationDuration) {
    return bannerWrapper.value?.fields.bannerRotationDuration
  }
  if (bannerCount.value > 1) {
    return BANNER_ROTATION_DURATION
  }
  return 0
})

const validBanners = computed(() => {
  return banners.value.filter(
    (banner) =>
      banner !== null && banner !== undefined && banner?.fields?.isActive,
  )
})

const currentBanner = computed(() => {
  if (validBanners.value.length === 0) {
    return null
  }
  return validBanners.value[currentBannerIndex.value]
})

let rotationInterval: number | null = null

const startRotation = () => {
  if (
    validBanners.value.length <= 1 ||
    bannerRotationDuration.value === 0 ||
    !window
  ) {
    return
  }

  rotationInterval = window.setInterval(() => {
    if (!isPaused.value) {
      rotateBanner()
    }
  }, bannerRotationDuration.value)
}

const rotateBanner = () => {
  if (validBanners.value.length <= 1) {
    return
  }

  isTransitioning.value = true

  // Wait for fade out animation to complete
  setTimeout(() => {
    currentBannerIndex.value =
      (currentBannerIndex.value + 1) % validBanners.value.length
    isTransitioning.value = false
  }, 300) // Match the transition duration
}

const pauseRotation = () => {
  isPaused.value = true
}

const resumeRotation = () => {
  isPaused.value = false
}

const stopRotation = () => {
  if (rotationInterval) {
    window.clearInterval(rotationInterval)
    rotationInterval = null
  }
}

onMounted(() => {
  startRotation()
})

onUnmounted(() => {
  stopRotation()
})
</script>

<template>
  <div
    class="flex h-9 w-full items-center justify-center font-medium text-white xs:h-auto xs:min-h-[34px] xs:p-2 md:min-h-10 md:px-0 md:py-2 lg:flex"
    :class="!currentBanner?.fields?.background_color ? 'bg-gray-600' : ''"
    :style="{ backgroundColor: currentBanner?.fields?.background_color }"
    data-testid="banner-wrapper"
  >
    <div
      class="mx-auto flex w-full max-w-screen-2xl justify-between xs:px-0 md:px-6"
    >
      <div
        class="z-10 col-span-1 col-start-1 row-span-1 row-start-1 hidden items-center gap-6 lg:flex"
      >
        <SFShopSwitcher listbox-button-aria-id="desktop-shop-switcher" />
        <SFLocalizedLink
          v-if="showStorefinder"
          :to="routeList.storefinder"
          raw
          class="group flex h-full items-center gap-2 text-white transition duration-100 ease-linear hover:text-orange-500"
          :aria-label="$t('user_area.store')"
        >
          <IconLocationMenu
            class="shrink-0 transition-colors duration-100 xs:size-5 md:size-6"
          />
          <span class="font-sans text-sm font-bold">
            {{ $t('user_area.store') }}
          </span>
        </SFLocalizedLink>
      </div>

      <div
        class="col-span-1 col-start-1 row-span-1 row-start-1 flex flex-1 flex-row items-center justify-center gap-x-4"
        @mouseenter="pauseRotation"
        @mouseleave="resumeRotation"
      >
        <Transition
          v-if="currentBanner"
          mode="out-in"
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <div
            v-if="currentBanner && !isTransitioning"
            :key="currentBanner.sys.id"
          >
            <CMSBanner
              :blok="currentBanner"
              :published-at="currentBanner.sys.createdAt"
            />
          </div>
        </Transition>
      </div>

      <div
        class="col-span-1 col-start-1 row-span-1 row-start-1 hidden items-center gap-x-7 justify-self-end lg:flex"
      >
        <SFHeaderUserIcons />
      </div>
    </div>
  </div>
</template>
